import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import MUIDataTable from "mui-datatables";
import {
  Grid,
  Button,
  Icon,
  IconButton,
  Tooltip,
  Paper,
  Chip,
  List,
  ListItem,
  ListItemText,
  Divider,
} from "@material-ui/core";
import { setApplicationLoading } from "../../../components/ApplicationLoading";
import InputAutoComplete from "../../../components/Inputs/InputAutoComplete";
import {
    createTFGInstructorAllocation, deleteTFGInstructorAllocation,
    getTFGInstructorInfo,
    getTFGInstructors,
    updateTFGInstructorAllocation,
    uploadInstructorsCSV,
} from "../../../services/TFG";
import { useNotification } from "../../../Utils/hooks";
import { copyToClipboard } from "../../../Utils/utils";
import TFGConfigResultDialog from "./TFGConfigResultDialog/TFGConfigResultDialog";
import TFGAcademicYearSelector from "../TFGAcademicYearSelector/TFGAcademicYearSelector";
import InstructorAllocationDialog from "./TFGInstructorAllocationDialog/InstructorAllocationDialog";
import TFGInstructorAllocationCreateDialogHook from "./TFGInstructorAllocationCreateDialog/useTFGInstructorAllocationCreateDialog";
import classNames from "classnames";
import { MUIDatatableLabels } from "../../../Utils/Table.utils";

const CSVKeys = ["academicYearId", "externalId", "limitRequestsTFG"];

const TFGConfig = () => {
  const [managers, setManagers] = useState([]);
  const [selected, setSelected] = useState(null);
  const [coord, setCoord] = useState(null);
  const [showResultDialog, setShowResultDialog] = useState(false);
  const [selectedTerm, setSelectedTerm] = useState("");
  const [allocationDialog, setAllocationDialog] = useState({
    show: false,
    allocation: undefined,
  });
  const [results, setResults] = useState({ success: [], failed: [] });
  const notification = useNotification();
  const fileUploadRef = useRef(null);

  const retrieveManagers = useCallback(async () => {
    // Do not call service if term is not selected
    if (!selectedTerm) {
      setManagers([]);
      return;
    }
    try {
      setApplicationLoading(true);
      const result = await getTFGInstructors(selectedTerm).then((res) =>
        res.map((item) => ({ value: item.externalId, label: item.name }))
      );
      if (!result) throw new Error();
      const ordered = result.sort((first, second) =>
        first.label.localeCompare(second.label)
      );
      setManagers(ordered);
    } catch {
      notification("error", "Ha habido un error al obtener los profesores");
    } finally {
      setApplicationLoading(false);
    }
  }, [notification, selectedTerm]);

  useEffect(() => {
    retrieveManagers();
  }, [retrieveManagers]);

  useEffect(() => {
    async function retrieveManagerInfo(id) {
      try {
        setApplicationLoading(true);
        const result = await getTFGInstructorInfo(id);
        if (!result) throw new Error();
        setCoord(result);
      } catch {
        notification(
          "error",
          "Ha habido un error al obtener la información del coordinador"
        );
      } finally {
        setApplicationLoading(false);
      }
    }

    selected && retrieveManagerInfo(selected);
  }, [selected, notification]);

  useEffect(() => {
    if (!selected) setCoord(null);
  }, [selected]);

  const allocations = useMemo(() => {
    if (coord && selectedTerm) {
      return coord.allocations.filter(
        (item) => item.academicYearId === selectedTerm
      );
    }

    return null;
  }, [coord, selectedTerm]);

  const onUploadClick = (event) => {
    event.preventDefault();

    if (fileUploadRef.current) {
      fileUploadRef.current.click();
    }
  };

  const onCopyClick = () => {
    copyToClipboard(CSVKeys.join(","));
    notification("info", "Las cabeceras se han copiado al portapapeles");
  };

  const onFilesChange = async (event) => {
    const files = event.target.files;

    if (files) {
      const file = files.item(0);
      if (file) {
        try {
          setApplicationLoading(true);
          const result = await uploadInstructorsCSV(file);
          if (!result) throw new Error();
          setResults(result);
          setShowResultDialog(true);
          retrieveManagers();
        } catch {
          notification("error", "Ha habido un error al subir el CSV");
        } finally {
          setApplicationLoading(false);
        }
      }
    }
  };

  const onHideResultsDialog = () => {
    setShowResultDialog(false);
    setTimeout(() => setResults({ success: [], failed: [] }), 250);
  };

  const onHideAllocationDialog = () => {
    setAllocationDialog({
      show: false,
      allocation: undefined,
    });
  };

  const onSaveAllocationDialog = async (allocation) => {
    try {
      setApplicationLoading(true);
      const body = {
        academicYearId: selectedTerm,
        limit: allocation.limit ? parseInt(allocation.limit) : null,
        degreeIds: allocation.degrees.map((degree) => degree.externalId),
      };
      const result = await updateTFGInstructorAllocation(
        selected,
        allocation.id,
        body
      );
      if (!result) throw new Error();
      notification(
        "success",
        "La asignación del instructor ha sido guardada correctamente"
      );
      setCoord((prev) => {
        const updated = { ...prev };
        const index = updated.allocations.findIndex(
          (item) => item.academicYearId === result.academicYearId
        );

        if (~index) {
          updated.allocations[index] = result;
        }

        return updated;
      });
      onHideAllocationDialog();
    } catch {
      notification(
        "error",
        "Ha habido un error al actualizar la asignación del instructor"
      );
    } finally {
      setApplicationLoading(false);
    }
  };

  const onCreateAllocation = async (allocation) => {
    try {
      setApplicationLoading(true);
      const body = {
        academicYearId: selectedTerm,
        limit: allocation.limit ? parseInt(allocation.limit) : null,
        degreeIds: allocation.degrees.map((degree) => degree.externalId),
      };
      const result = await createTFGInstructorAllocation(selected, body);
      if (!result) throw new Error();
      notification(
        "success",
        "La asignación del instructor ha sido guardada correctamente"
      );
      // Refresh coordinator info to reflect latest allocations from backend
      try {
        const refreshed = await getTFGInstructorInfo(selected);
        if (refreshed) setCoord(refreshed);
      } catch (e) {
        // If refresh fails, keep previous optimistic state (no-op)
      }
      return result;
    } catch {
      notification(
        "error",
        "Ha habido un error al crear la asignación del instructor"
      );
      return false;
    } finally {
      setApplicationLoading(false);
    }
  };

  // Hook to manage the create dialog visibility and rendering
  const {
    open: openCreateAllocationDialog,
    DialogElement: CreateAllocationDialogElement,
  } = TFGInstructorAllocationCreateDialogHook({
    coord,
    academicYear: selectedTerm,
    onSave: onCreateAllocation,
  });

  const handleAllocationDialogSubmit = (allocation, isCreate) =>
    isCreate
      ? onCreateAllocation(allocation)
      : onSaveAllocationDialog(allocation);

  function renderDegrees(degrees) {
    return (
      <Paper elevation={0} style={{ maxHeight: 300, overflow: "auto" }}>
        <List dense>
          {degrees?.map((degree, index) => (
            <React.Fragment key={degree.externalId}>
              <ListItem>
                <ListItemText
                  primary={
                    <div style={{ display: "flex", alignItems: "center" }}>
                      <Chip
                        label={degree.externalId}
                        size="small"
                        color="primary"
                        style={{ marginRight: 8 }}
                      />
                      <span>{degree.name}</span>
                    </div>
                  }
                />
              </ListItem>
              {index < degrees.length - 1 && <Divider component="li" />}
            </React.Fragment>
          ))}
        </List>
      </Paper>
    );
  }

  function renderRequests(requests) {
    const filteredRequests = (requests || []).filter((r) => {
      if (r == null) return false;
      if (typeof r === "string") return r.trim().length > 0;
      // For non-string values, keep truthy ones only
      return Boolean(r);
    });

    return (
      <Paper elevation={0} style={{ maxHeight: 300, overflow: "auto" }}>
        <List dense>
          {filteredRequests.map((request, index) => (
            <React.Fragment key={`request-${index}`}>
              <ListItem>
                <ListItemText
                  primary={
                    <Chip
                      label={request}
                      variant="outlined"
                      color="secondary"
                      style={{ fontWeight: 500 }}
                    />
                  }
                />
              </ListItem>
              {index < filteredRequests.length - 1 && <Divider component="li" />}
            </React.Fragment>
          ))}
        </List>
      </Paper>
    );
  }

  const onEditClick = (id) => {
    const allocation = allocations.find((item) => item.id === id);
    setAllocationDialog({
      show: true,
      allocation,
    });
  };

  const onDeleteClick = (id) => {
      setApplicationLoading(true);
      const result = deleteTFGInstructorAllocation(selected,id)
      if (!result) throw new Error();
      notification(
          "success",
          "La asignación del instructor ha sido guardada correctamente"
      );
      setApplicationLoading(false);
  };

  const options = {
    selectableRows: "none",
    selectableRowsOnClick: false,
    textLabels: {
      ...MUIDatatableLabels,
      body: {
        ...MUIDatatableLabels.body,
        noMatch: "No se han encontrado asignaciones",
      },
    },
    print: false,
    download: false,
    filter: false,
  };

  const columns = [
    {
      name: "id",
      options: {
        display: "excluded",
        filter: false,
      },
    },
    {
      name: "degrees",
      label: "Titulaciones asociadas",
      options: {
        filter: false,
        customBodyRender: (value, tableMeta, updateValue) => {
          return renderDegrees(value);
        },
      },
    },
    {
      name: "requests",
      label: "Peticiones asignadas",
      options: {
        filter: false,
        customBodyRender: (value, tableMeta, updateValue) => {
          return renderRequests(value);
        },
      },
    },
    {
      name: "limit",
      label: "Limite de peticiones",
    },
    {
      name: "id",
      label: "Acciones",
      options: {
        customBodyRender: (id) => (
          <div className="actions">
            {/* <Tooltip title="Editar" aria-label="edit">
              <IconButton aria-label="edit" onClick={() => onEditClick(id)}>
                <Icon className="edit-icon">
                  edit
                </Icon>
              </IconButton>
            </Tooltip> */}
            <Tooltip title="Configurar intervalos" aria-label="edit">
              <IconButton aria-label="edit" onClick={() => onEditClick(id)}>
                <Icon className="edit-icon">edit</Icon>
              </IconButton>
            </Tooltip>
            <Tooltip title="Eliminar" aria-label="delete">
              <IconButton aria-label="delete" onClick={() => onDeleteClick(id)}>
                <Icon className="delete-icon">delete</Icon>
              </IconButton>
            </Tooltip>
          </div>
        ),
      },
    },
  ];

  const TableClass = classNames("tfg-allocations-table");

  return (
    <div className="tfg-config">
      {/*  { (!!selectedTerm && !!coord) && (
          <TFGInstructorAllocationCreateDialog
              coord={coord}
              academicYear={selectedTerm}
              visible={showAllocationDialog}
              onHide={() => setShowAllocationDialog(false)}
          />
      )} */}
      <InstructorAllocationDialog
        selectedCoord={coord}
        selectedTerm={selectedTerm}
        visible={allocationDialog.show}
        onHide={onHideAllocationDialog}
        onSave={handleAllocationDialogSubmit}
        selectedAllocation={allocationDialog.allocation}
      />
      {CreateAllocationDialogElement}
      <TFGConfigResultDialog
        visible={showResultDialog}
        results={results}
        onHide={onHideResultsDialog}
      />
      <div className="upload-csv">
        <p className="disclaimer">
          Cargue un archivo con las cabeceras:&nbsp;
          <i>{CSVKeys.join(", ")}</i>
          <br />
          <br />
          Puedes utilizar el botón inferior para copiar las cabeceras requeridas
          al portapapeles
        </p>
        <div className="upload-csv__actions">
          <Tooltip title="Copiar cabeceras al portapapeles" aria-label="copy">
            <IconButton aria-label="copy">
              <Icon className="copy-icon" onClick={onCopyClick}>
                content_copy
              </Icon>
            </IconButton>
          </Tooltip>
          <input
            type="file"
            onChange={onFilesChange}
            multiple={false}
            style={{ display: "none" }}
            ref={fileUploadRef}
            accept=".csv"
          />
          <Button onClick={onUploadClick} variant="contained" color="primary">
            Subir CSV
          </Button>
        </div>
      </div>
      <div className="update-instructor">
        <h3>Buscar director</h3>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TFGAcademicYearSelector
              selectedTerm={selectedTerm}
              setSelectedTerm={setSelectedTerm}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <InputAutoComplete
              key="instructor"
              name="instructor"
              value={selected}
              onChange={setSelected}
              label="Selecciona un director"
              options={managers}
              disabled={!managers.length}
            />
          </Grid>
        </Grid>
        {!!selectedTerm && !!coord && (
          <div className="actions">
            <Button
              variant="contained"
              color="primary"
              onClick={openCreateAllocationDialog}
            >
              Crear asignación
            </Button>
          </div>
        )}
        {!!allocations?.length && (
          <div className="tfg-allocations-table-container">
            <MUIDataTable
              className={TableClass}
              title={`Asignacion docente para ${coord.instructorName}`}
              columns={columns}
              options={options}
              data={allocations}
            />
          </div>
        )}
        {!allocations?.length && !!coord && !!selectedTerm && (
          <p className="empty-list">
            No hay límites configurados para este profesor en el año académico
            seleccionado
          </p>
        )}
      </div>
    </div>
  );
};

export default TFGConfig;
